import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getClientId } from "./clientId";
import { useAuth } from "../context/AuthContext";
import { fetchTrash, addTrash, removeTrash, fetchCommunityTrash } from "./api";

export const useTrash = () => {
  const clientId = getClientId();
  const qc = useQueryClient();
  const { requireAuth } = useAuth();

  const { data } = useQuery({ queryKey: ["trash", clientId], queryFn: () => fetchTrash(clientId) });
  const trashedSet = new Set(data?.mints || []);
  const items = data?.items || [];
  const reasonMap = {};
  items.forEach((i) => { reasonMap[i.mint] = i.reason || ""; });

  const refresh = () => qc.invalidateQueries({ queryKey: ["trash", clientId] });

  const trash = async (mint, symbol, reason = "") => {
    if (!requireAuth("mark tokens as trash")) return;
    await addTrash(clientId, { mint, symbol, reason });
    refresh();
    toast.success(`${symbol || "Token"} marked as trash — hidden from your Radar`);
  };

  const untrash = async (mint) => {
    await removeTrash(clientId, mint);
    refresh();
    toast.success("Restored to your Radar");
  };

  const isTrashed = (mint) => trashedSet.has(mint);
  const reasonFor = (mint) => reasonMap[mint] || "";

  return { trashedSet, items, trash, untrash, isTrashed, reasonFor };
};

export const useCommunityTrash = () => {
  const { data } = useQuery({
    queryKey: ["communityTrash"],
    queryFn: fetchCommunityTrash,
    refetchInterval: 120000,
    staleTime: 60000,
  });
  const counts = data?.counts || {};
  const topReasons = data?.topReasons || {};
  const isCommunityScam = (mint) => !!counts[mint];
  const scamCount = (mint) => counts[mint] || 0;
  const scamReason = (mint) => topReasons[mint] || "";
  return { counts, isCommunityScam, scamCount, scamReason, min: data?.min || 3 };
};
